<script setup lang="ts">
/*
* Компонент AuditGroupTable - Таблица групп аудитов
*
* Основные функции:
* - Отображение списка групп аудитов с пагинацией и сортировкой
* - Фильтрация данных по названию, нормативу, разделу, участникам и т.д.
* - Переход на детальную страницу группы аудитов
*/

// Импорт утилиты для извлечения реактивных свойств из хранилища Pinia
import { storeToRefs } from 'pinia'
// Импорт базовых функций Vue
import { computed, onMounted } from 'vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoReload } from '@/modules/onboarding/composables/useOnboardingDemoReload'
import { AUDITS_ANCHORS, AUDITS_CONTEXTS } from '@/modules/onboarding/scenarios/audits/anchors'
// Импорт маршрутизатора Vue
import { useRouter } from 'vue-router'
// Импорт хуков Vuetify для работы с темой и размерами экрана
import { useDisplay, useTheme } from 'vuetify/framework'

// Импорт хранилища групп аудитов
import { useAuditGroupStore } from '@/modules/audits/stores/useAuditGroupStore/useAuditGroupStore'
// Импорт хранилища для глобального состояния загрузки
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'


import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import CollapsibleCell from '@/components/createdUi/CollapsibleCell.vue'
import CollapsibleText from '@/components/createdUi/CollapsibleText.vue'
import { formatShortName } from '@/utils/formatters'

// Импорт composable для управления логикой фильтров таблицы
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
// Импорт перечисления названий маршрутов
import { ERouteNames } from '@/router/ERouteNames'

// Инициализация хука темы Vuetify
const theme = useTheme()
// Определение текущего размера экрана
const { smAndDown } = useDisplay()
// Инициализация маршрутизатора
const router = useRouter()

// Инициализация хранилища обратной связи
const feedbackStore = useFeedbackStore()
// Инициализация хранилища групп аудитов
const groupStore = useAuditGroupStore()

// Ссылки на пагинацию и сортировку (извлечение реактивных состояний из хранилища)
const { currentSortString, nextCursor } = storeToRefs(groupStore)
const { itemsPerPage } = useTableItemsPerPage('auditGroup', 25)

// Синхронизация с хранилищем групп аудитов
groupStore.itemsPerPage = itemsPerPage.value
watch(itemsPerPage, newVal => {
  groupStore.itemsPerPage = newVal
})

// Конфигурация фильтров для таблицы
const filterConfigs: IFilterConfig[] = [
  { key: 'name', labelPrefix: 'Название: ', type: 'tree' },
  { key: 'standards', labelPrefix: 'Норматив: ', type: 'tree' },
  { key: 'parents', labelPrefix: 'Предмет проверки: ', type: 'tree' },
  { key: 'auditors', labelPrefix: 'Участник: ', type: 'tree' },
]

// Инициализация логики таблицы (обработка фильтров, сортировки, пагинации)
const tableLogic = useTableFilters({
  // Реактивный список текущих фильтров
  filters: computed(() => groupStore.filters),
  // Выборки для фильтров поиска
  searchChoices: computed(() => groupStore.searchChoices) as any,
  // Выборки для фильтров по дереву
  filterChoices: computed(() => groupStore.filterChoices) as any,
  // Состояние загрузки фильтров
  filterLoading: groupStore.filterLoading,
  // Метод для получения вариантов фильтров с сервера
  fetchFilterChoices: (key: string) => groupStore.fetchFilterChoices(key),
  // Метод получения данных таблицы с сервера
  fetchData: (cursor?: string, limit?: number) => groupStore.fetchAuditsGroups(cursor, limit),
  // Строка текущей сортировки
  currentSortString,
  // Количество элементов на странице
  itemsPerPage,
  // Метод сброса фильтров
  resetFilters: () => groupStore.resetFilters(),
  // Курсор для следующей страницы данных
  nextCursor,
  // Количество загруженных элементов
  loadedItemsCount: computed(() => groupStore.auditsGroup.length),
  // Общее количество элементов на сервере
  totalItemsCount: computed(() => groupStore.totalGroupCount),
  // Карта сопоставления полей для сортировки
  sortMap: {
    name: 'name',
    standards: 'standards',
    parents: 'parents',
    auditors: 'auditors',
  },
  // Передача конфигурации фильтров
  filterConfigs,
})

// Конфигурация колонок таблицы
const headers = computed(() => [
  { title: 'Название', key: 'name', sortable: false, filterType: 'tree', filterKey: 'name' },
  { title: 'Норматив', key: 'standards', sortable: false, filterType: 'tree', filterKey: 'standards' },
  { title: 'Предмет проверки', key: 'parents', sortable: false, filterType: 'tree', filterKey: 'parents' },
  { title: 'Участники', key: 'auditors', sortable: false, filterType: 'tree', filterKey: 'auditors' },
] as any[])

// Вычисляемое свойство: список видимых групп аудитов для текущей страницы
const displayedGroups = computed(() => {
  const start = (tableLogic.currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return groupStore.auditsGroup.slice(start, end)
})

// Обработчик клика по строке таблицы
const handleRowClick = async (item: any) => {
  if (item.id) {
    await groupStore.fetchAuditGroupById(item.id)
    await router.push({
      name: ERouteNames.AUDIT_GROUP_DETAIL,
      params: { id: item.id },
    })
  }
}

// Привязка пропсов к строкам таблицы (в данном случае - обработчика клика)
const rowProps = (data: { item: any }) => ({
  onClick: () => handleRowClick(data.item),
})

// Инициализация: загрузка данных при монтировании компонента
useOnboardingContext({ names: [AUDITS_CONTEXTS.groups], root: () => document.body })
useOnboardingDemoReload('audit', () => groupStore.fetchAuditsGroups())

onMounted(async () => {
  await groupStore.fetchAuditsGroups()
})
</script>

<template>
  <div class="wrapper">
    <div>
      <!-- v-if/v-else вместо Teleport :disabled — иначе на мобилке дублируются фильтры -->
      <Teleport
        v-if="smAndDown"
        defer
        to="#audit-mobile-filters"
      >
        <VExpansionPanels class="w-100">
          <VExpansionPanel>
            <VExpansionPanelTitle class="d-flex align-center py-2 px-4">
              <div class="d-flex align-center ga-2">
                <VIcon icon="ri-filter-3-line" size="20" />
                <span class="text-subtitle-1 font-weight-medium">Фильтры</span>
              </div>
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <div class="d-flex flex-column ga-2 w-100">
                <VTextField
                  v-model="tableLogic.searchFilter.value"
                  class="w-100"
                  variant="outlined"
                  clearable
                  placeholder="Фильтр по ключевому слову"
                  :data-onboarding="AUDITS_ANCHORS.table.search"
                  autocomplete="off"
                  autocapitalize="off"
                  autocorrect="off"
                  spellcheck="false"
                  type="text"
                  @keyup.enter="tableLogic.applySearchFilter()"
                />

                <VIconBtn
                  v-if="tableLogic.searchFilter.value"
                  class="w-100 rounded-sm"
                  color="primary"
                  icon="ri-filter-2-line"
                  variant="outlined"
                  :rounded="false"
                  @click="tableLogic.applySearchFilter()"
                />

                <VBtn
                  v-if="tableLogic.hasActiveFilters.value"
                  class="w-100 text-color-secondary"
                  prepend-icon="ri-delete-bin-line"
                  variant="outlined"
                  color="secondary"
                  :loading="feedbackStore.isGlobalLoading"
                  :data-onboarding="AUDITS_ANCHORS.table.resetFilters"
                  @click="tableLogic.renewDataAndResetFilters()"
                >
                  Сбросить фильтры
                </VBtn>
              </div>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </Teleport>

      <div
        v-else
        class="d-flex align-center gap-2 w-100"
        :class="tableLogic.activeFilterChips.value.length ? 'px-4 pt-4' : 'pa-4'"
      >
          <VTextField
            v-model="tableLogic.searchFilter.value"
            class="flex-grow-0 w-25"
            variant="outlined"
            clearable
            placeholder="Фильтр по ключевому слову"
          :data-onboarding="AUDITS_ANCHORS.table.search"
            autofocus
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            type="text"
            @keyup.enter="tableLogic.applySearchFilter()"
          />

          <VIconBtn
            v-if="tableLogic.searchFilter.value"
            color="primary"
            class="rounded-sm"
            icon="ri-filter-2-line"
            variant="outlined"
            :rounded="false"
            width="48"
            height="48"
            @click="tableLogic.applySearchFilter()"
          />

          <VBtn
            v-if="tableLogic.hasActiveFilters.value"
            class="ml-auto text-color-secondary"
            prepend-icon="ri-delete-bin-line"
            variant="outlined"
            color="secondary"
            :loading="feedbackStore.isGlobalLoading"
          :data-onboarding="AUDITS_ANCHORS.table.resetFilters"
            @click="tableLogic.renewDataAndResetFilters()"
          >
            Сбросить фильтры
          </VBtn>
        </div>

      <div v-if="tableLogic.activeFilterChips.value.length" class="filter-chip d-flex ml-4 align-center gap-2 py-3">
        <VChip
          v-for="chip in tableLogic.activeFilterChips.value"
          :key="`${chip.type}-${chip.value || 'no-value'}`"
          closable
          @click:close="tableLogic.removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>
    </div>

    <VDataTableServer
        :data-onboarding="AUDITS_ANCHORS.table.content"
      :items="displayedGroups"
      :items-length="groupStore.totalGroupCount"
      :headers="headers as any"
      :items-per-page="itemsPerPage"
      :row-props="rowProps"
      class="no-row-gaps cursor-pointer-table custom-data-table custom-data-table-footer custom-data-table"
      item-value="id"
      :class="[{ 'dark-theme': theme.global.current.value.dark }]"
      @update:options="tableLogic.handlePageChange"
    >
      <template #no-data>
        <div class="text-center text-italic py-4">
          Данные отсутствуют
        </div>
      </template>

      <!-- ДИНАМИЧЕСКИЕ ЗАГОЛОВКИ ФИЛЬТРОВ ИЗ COMPOSABLE -->
      <template v-for="col in headers.filter(h => h.filterType)" :key="col.key" #[`header.${col.key}`]="{ column }">
        <div class="header-content d-flex align-center gap-1">
          <span>{{ column.title }}</span>

          <VIcon
            v-if="column.sortable"
            size="16"
            class="ml-1 custom-sort-icon"
            :icon="tableLogic.getSortIcon(col.key)"
            :class="{
              'is-active': tableLogic.isSortActive(col.key),
              'is-desc': tableLogic.isSortDesc(col.key),
            }"
          />

          <!-- Спиннер -->
          <div v-if="tableLogic.filterLoading?.[col.filterKey!]" class="d-flex align-center justify-center ml-1" style="width: 28px; height: 28px">
            <VProgressCircular
              indeterminate
              color="primary"
              size="20"
              width="2"
            />
          </div>

          <!-- Иконки/Меню (Тут везде только Tree) -->
          <VIconBtn
            v-else
            variant="plain"
            size="small"
            class="filter-icon-btn"
            :icon="tableLogic.getFilterIcon(col.filterKey!)"
            @click.stop="tableLogic.handleFilterClick(col.filterKey!)"
          />

          <VMenu
            v-model="tableLogic.filterMenus.value[col.filterKey!]"
            :close-on-content-click="false"
            location="bottom start"
            transition="scale-transition"
          >
            <template #activator="{ props: menuProps }">
              <div v-bind="menuProps" />
            </template>
            <UniversalFilterTree
              :field-key="col.filterKey!"
              module="audit-group"
              :store="groupStore"
              :model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] || []"
              :placeholder="`Поиск: ${column.title}`"
              @update:model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] = $event"
              @apply="tableLogic.applyFilter(col.filterKey!)"
            />
          </VMenu>
        </div>
      </template>
      <!-- КОНЕЦ ДИНАМИЧЕСКИХ ЗАГОЛОВКОВ -->

      <!-- ================= CELLS ================= -->

      <template #[`item.name`]="{ item }">
        <CollapsibleText
          v-if="item.name"
          :text="item.name"
        />
        <span v-else>-</span>
      </template>

      <template #[`item.standards`]="{ item }">
        <CollapsibleText
          v-if="item.standards?.length"
          :text="item.standards.map(s => s.label).join(', ')"
        />
        <span v-else>-</span>
      </template>

      <template #[`item.parents`]="{ item }">
        <CollapsibleCell
          :items="item.parents"
          :collapsed-items-count="8"
          type-label="предметов проверки"
          btn-class="audit-collapsible-trigger"
        />
      </template>

      <template #[`item.auditors`]="{ item }">
        <CollapsibleText
          v-if="item.auditors?.length"
          :text="item.auditors.map(auditor => formatShortName(auditor)).filter(Boolean).join(', ')"
        />
        <span v-else>-</span>
      </template>

      <!-- FOOTER -->
      <template #bottom>
        <div class="custom-data-table-footer" :class="{ 'hide-next-button': tableLogic.hideNextButton.value }">
          <VDataTableFooter
            :show-current-page="true"
            :items-per-page-options="[25, 50, 100]"
            v-bind="$attrs"
            @click:prev="$emit('click:prev', $event)"
            @click:next="$emit('click:next', $event)"
            @click:first="$emit('click:first', $event)"
            @click:last="$emit('click:last', $event)"
          />
        </div>
      </template>
    </VDataTableServer>
  </div>

  <RouterView />
</template>

<style scoped lang="scss">
/* ===== BASE TABLE ===== */
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
  white-space: nowrap;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
  padding-block: 0.75rem !important;
}

:deep(.audit-collapsible-trigger) {
  text-decoration: none !important;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

/* ===== TEXT BEHAVIOR ===== */
:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

/* ===== ROW HOVER ===== */
:deep(.custom-data-table tr:hover) {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

/* ===== FOOTER ===== */
:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination { order: 1; }
    .v-data-table-footer__items-per-page { order: 2; }
    .v-data-table-footer__info { order: 3; }

    .v-pagination__last > button,
    .v-pagination__next > button[aria-disabled="true"] {
      display: none !important;
    }
  }
}

:deep(.custom-data-table td .cell-wrap) {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 4px;
}

/* ===== COLUMN WIDTHS (Audit Group) ===== */
:deep(.v-data-table__th:nth-child(1)) { /* Название */
  min-width: 6rem !important;
}

:deep(.v-data-table__th:nth-child(2)) { /* Норматив */
  min-width: 6rem !important;
}

:deep(.v-data-table__th:nth-child(3)) { /* Предмет проверки */
  min-width: 12rem !important;
}

:deep(.v-data-table__th:nth-child(4)) { /* Участники */
  min-width: 12rem !important;
}

/* ===== SORT ICONS ===== */

/* скрываем стандартные иконки vuetify */
:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

/* кастомная иконка сортировки */
.custom-sort-icon {
  font-size: 16px;
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  opacity: 0.15;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  &.is-active {
    opacity: 1 !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

:deep(.v-data-table__th--sortable:hover) .custom-sort-icon {
  opacity: 0.6;
}

/* ===== FILTER ICONS ===== */
:deep(.v-data-table__th .v-btn) {
  opacity: 0.5;
  transition: opacity 0.2s ease;
}

:deep(.v-data-table__th:hover .v-btn) {
  opacity: 1;
}

/* ===== UTILITY ===== */
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-chip {
  overflow-x: auto;
  white-space: nowrap;
}

.filter-chip .v-chip {
  flex-shrink: 0;
  max-width: none !important;
  white-space: nowrap;
  overflow: visible;
}
</style>
